import _ from 'lodash'
import { sha1 } from 'object-hash'
import PropTypes from 'prop-types'
import React, { createRef } from 'react'
import { connect } from 'react-redux'

import { FormKeyContext, FormTypeContext } from '../../../lib/form-context'
import { withDiscardDialog } from '../../../lib/global-discard-dialog'
import MagicGrid from '../MagicGrid'
import { Button, FullWidthCircularProgress } from '../NarusMaterial'

import * as Actions from './actions'
import pluralize from './pluralize'

const mapStateToProps = (redux, ownProps) => {
  const pluralEntryType = pluralize.plural(ownProps.entryType)
  const { entrySet } = ownProps
  const data = redux.crudTable[pluralEntryType] || {}

  return {
    getNewEntries: data.getNewEntries,
    loading: data.loading,
    entries: data.entries,
    errors: data.errors || {},
    newEntryFormVisible: (
      (entrySet && ((data.set || {})[entrySet] || {})) ||
      data
    ).newEntryFormVisible,
    crudSortOn: data.sortOn
  }
}

@withDiscardDialog
@connect(mapStateToProps)
class CrudTable extends React.Component {
  constructor (props) {
    super(props)
    this.state = {}
    if (props.callbacks) {
      props.callbacks({ collapse: this.collapse })
    }

    this.magicGrid = createRef()
  }

  componentDidMount () {
    this.createDeleteDialogComponent(this.props)

    this.props.dispatch(Actions.changeSortState(
      this.props.entryType,
      this.props.sortOn
    ))

    this.props.dispatch(Actions.getEntries(
      this.props.entryType,
      this.props.urlFns,
      this.props.mapFns
    ))

    if (!this.props.sortOn) {
      this.props.dispatch(Actions.resetSortState(this.props.entryType))
    }
  }

  componentDidUpdate (prevProps, prevState) {
    this.createDeleteDialogComponent(this.props)

    if (this.props.updateKey && this.props.updateKey !== prevProps.updateKey) {
      this.props.dispatch(Actions.getEntries(
        this.props.entryType,
        this.props.urlFns,
        this.props.mapFns
      ))
    }
  }

  handleSubmit = (data, index) => {
    if (data.id) {
      this.props.dispatch(Actions.updateEntry(
        this.props.entryType,
        data,
        this.props.urlFns,
        this.props.mapFns,
        this.props.callbackFn
      ))
      this.magicGrid.current?.collapse(index)
      return
    }

    this.props.dispatch(Actions.createEntry(
      this.props.entryType,
      this.props.entrySet,
      data,
      this.props.urlFns,
      this.props.mapFns,
      this.props.callbackFn,
      index
    ))
  }

  handleCancel = index => {
    let { discardDialog, entryType, entrySet, dispatch } = this.props
    let formKey = `${entryType}_${index}`

    if (index === 'newEntry') {
      discardDialog.show(formKey, () => {
        dispatch(Actions.hideNewEntryForm(entryType, entrySet))
      })

      return
    }

    discardDialog.show(formKey, () => {
      this.magicGrid.current?.collapse(index)
    })
  }

  handleCollapseRow = index => {
    let { discardDialog, entryType } = this.props
    let formKey = `${entryType}_${index}`

    if (discardDialog.isFormDirty(formKey)) {
      this.handleCancel(index)
      return
    }

    this.magicGrid.current?.collapse(index)
  }

  handleNewEntry = () => {
    this.props.dispatch(Actions.showNewEntryForm(
      this.props.entryType,
      this.props.entrySet
    ))
  }

  handleDelete = (row, formData) => {
    const { entryType } = this.props

    this.props.dispatch(Actions.deleteEntry(
      entryType,
      this.props.urlFns,
      this.props.mapFns,
      row,
      formData,
      this.props.callbackFn
    ))
  }

  createDeleteDialogComponent = props => {
    const { deleteDialog, additionalDeleteDialogProps } = props
    const hash = sha1([ deleteDialog, additionalDeleteDialogProps ])

    if (this.state.deleteDialogComponentHash !== hash) {
      const deleteDialogDecorator = dialogProps => {
        const ddProps = {
          ...additionalDeleteDialogProps,
          ...dialogProps
        }

        return React.createElement(deleteDialog, ddProps)
      }

      this.setState({
        deleteDialogComponentHash: hash,
        deleteDialogComponent: deleteDialogDecorator
      })
    }
  }

  collapse = callback => {
    let { discardDialog, entryType } = this.props
    let formKey = `${entryType}_${'index'}`

    if (discardDialog.isFormDirty(formKey)) {
      discardDialog.show(formKey, () => {
        this.doCollapse(callback)
      })

      return
    }

    this.doCollapse(callback)
  }

  doCollapse = callback => {
    const { dispatch, entryType, entrySet } = this.props
    dispatch(Actions.hideNewEntryForm(entryType, entrySet))
    this.magicGrid.current?.collapse()
    callback()
  }

  deleteSwitch = () => {
    const { readOnly, noDelete } = this.props

    if (readOnly) {
      return false
    }

    return !noDelete
  }

  render () {
    let {
      loading,
      entryType,
      initialValues,
      labelButton,
      labelNoEntries,
      entries,
      errors,
      disabled,
      filterFn,
      form: FormComponent
    } = this.props

    let rows = filterFn
      ? (entries?.filter?.(filterFn) ?? [])
      : (entries ?? [])

    let displayNewEntry =
      !this.props.readOnly &&
      !disabled &&
      this.props.newEntryFormVisible

    let displayButton =
      !this.props.readOnly &&
      !this.props.noCreate &&
      !displayNewEntry

    let initialVals = _.isFunction(initialValues)
      ? initialValues() ?? {}
      : initialValues ?? {}

    let canDelete = this.deleteSwitch()

    return (
      <FormTypeContext.Provider value={entryType}>
        <div className='crud-table'>
          {loading && (
            <FullWidthCircularProgress />
          )}

          <MagicGrid
            ref={this.magicGrid}
            rows={rows}
            headers={this.props.tableDefinition}
            canDelete={canDelete}
            onDelete={this.handleDelete}
            itemName={
              labelNoEntries ||
              pluralize.plural(_.capitalize(entryType))
            }
            expansionComponent={FormComponent}
            expansionComponentProps={({ index }) => ({
              onSubmit: data => {
                this.props.discardDialog.resetForm(`${entryType}_${index}`, () => {
                  this.handleSubmit(data, index)
                })
              },
              onCancel: () => {
                this.handleCancel(index)
              },
              formKey: `${entryType}_${index}`,
              ...this.props.additionalFormProps
            })}
            deleteDialog={this.state.deleteDialogComponent}
            disableGrid={this.props.readOnly}
            sortOn={this.props.sortOn}
            sortDir={this.props.sortDir}
            collapseRowFn={this.handleCollapseRow}
          />

          <br />

          {displayNewEntry && (
            <FormKeyContext.Provider value='newEntry'>
              <FormComponent
                initialValues={initialVals}
                value={initialVals}
                magicGridIndex='newEntry'
                error={errors.newEntry}
                onSubmit={data => {
                  this.props.discardDialog.resetForm(`${entryType}_newEntry`, () => {
                    this.handleSubmit(data, 'newEntry')
                  })
                }}
                onCancel={() => {
                  this.handleCancel('newEntry')
                }}
                formKey={`${entryType}_newEntry`}
                {...this.props.additionalFormProps}
              />
            </FormKeyContext.Provider>
          )}

          {displayButton && (
            <Button
              purple
              label={`Add ${labelButton || _.capitalize(entryType)}`}
              onClick={this.handleNewEntry}
              disabled={disabled}
            />
          )}
        </div>
      </FormTypeContext.Provider>
    )
  }
}

CrudTable.propTypes = {
  tableDefinition: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  entryType: PropTypes.string.isRequired,
  urlFns: PropTypes.instanceOf(Object).isRequired,
  mapFns: PropTypes.instanceOf(Object).isRequired,
  readOnly: PropTypes.bool,
  filterFn: PropTypes.func,
  noDelete: PropTypes.bool,
  disabled: PropTypes.bool,
  sortOn: PropTypes.number,
  sortDir: PropTypes.string,
  // function called after an update or post for new table item
  callbackFn: PropTypes.func,
  entrySet: PropTypes.string,
  callbacks: PropTypes.func
}

CrudTable.defaultProps = {
  sortOn: 4
}

export default CrudTable
